import axios from "axios";
import type { AxiosRequestConfig } from "axios";
import showToast from "@utils/toast";
import CryptoJS from "crypto-js";
import * as API from "@utils/apiPath";
import {
  CSRF_STORAGE_KEY,
  ensureCsrfToken,
  refreshCsrfToken,
} from "@utils/csrfToken";
import { showLoading, hideLoading } from "@reducers/loading/loadingSlice";
import { setAccountLogin } from "@reducers/auth/authSlice";
import { logout } from "@reducers/user/userSlice";

const apiUrl = import.meta.env.VITE_API_URL;

type BoundDispatchStore = {
  dispatch: (action: unknown) => unknown;
};

let boundStore: BoundDispatchStore | null = null;

export function bindAgentStore(storeInstance: BoundDispatchStore): void {
  boundStore = storeInstance;
}

function getStore(): BoundDispatchStore {
  if (!boundStore) {
    throw new Error("Redux store is not bound to agent yet");
  }
  return boundStore;
}

type RetryableRequestConfig = {
  _retry?: boolean;
  _csrfRetry?: boolean;
  headers?: Record<string, string>;
  url?: string;
};

function messageText(data: unknown): string {
  const d = data as { message?: string | string[] } | undefined;
  const msg = d?.message;
  return Array.isArray(msg) ? msg.join(" ") : String(msg ?? "");
}

function isCsrfForbiddenResponse(error: {
  response?: { status?: number; data?: unknown };
}): boolean {
  if (error.response?.status !== 403) return false;
  const text = messageText(error.response.data);
  return /csrf/i.test(text);
}

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

const forceLogout = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("persist:tov-ride-admin-root");
  localStorage.removeItem("persist:TovRide-admin-root");
  try {
    sessionStorage.removeItem(CSRF_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  getStore().dispatch(setAccountLogin(false));
  getStore().dispatch(logout());
  window.location.href = "/signin";
};

const agent = axios.create({
  baseURL: `${apiUrl}/api/`,
  headers: {
    "Content-Type": "application/json",
    "access-control-allow-origin": "*",
    "Accept-Language": "en",
    deviceType: "web",
    Accept: "application/json",
  },
});

agent.interceptors.request.use(
  async (config) => {
    const secret = import.meta.env.VITE_CRYPTO_TOKEN;
    if (!secret) {
      console.error("Missing VITE_CRYPTO_TOKEN");
      return config;
    }
    const timestamp = Math.floor(Date.now() / 1000).toString();
    const signature = CryptoJS.HmacSHA256(
      timestamp,
      secret
    ).toString(CryptoJS.enc.Base64);
    const xAccessToken = `${timestamp}.${signature}`;
    config.headers = config.headers || {};
    config.headers["x-access-token"] = xAccessToken;
    config.headers["deviceType"] = "web";
    const authToken = localStorage.getItem("authToken");
    console.log("AUTH TOKEN =>", authToken);
    if (authToken) {
      config.headers["Authorization"] = `Bearer ${authToken}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    const extended = config as AxiosRequestConfig & {
      skipCsrfPrepare?: boolean;
    };
    const method = String(config.method ?? "get").toLowerCase();
    if (!extended.skipCsrfPrepare) {
      const url = String(config.url ?? "");
      if (
        ["post", "put", "patch", "delete"].includes(method) &&
        !url.includes("csrf/token")
      ) {
        await ensureCsrfToken();
        const csrf = sessionStorage.getItem(CSRF_STORAGE_KEY);
        if (csrf) {
          config.headers["x-csrf-token"] = csrf;
        }
      }
    }

    if (method !== "get" && method !== "head") {
      getStore().dispatch(showLoading());
    }
    return config;
  },
  async (error) => {
    getStore().dispatch(hideLoading());
    return Promise.reject(error);
  }
);



agent.interceptors.response.use(
  (response) => {
    if (response.config.method !== "get") {
      const url = response.config.url ?? "";
      const skipSuccessToast =
        url.includes("notification/admin/mark-read") ||
        url.includes(API.ADMIN_REFRESH_TOKEN) ||
        url.includes(API.BLOG_IMAGE_UPLOAD);
      if (!skipSuccessToast) {
        const data = response?.data as { message?: string };
        const message = data?.message ?? "Something went wrong!";
        showToast(`${message}`, "success");
      }
      getStore().dispatch(hideLoading());
    }
    return response;
  },
  async (error) => {
    getStore().dispatch(hideLoading());
    if (error.isAxiosError && error.response?.status === 422) {
      const validationError = error.response.data;
      const errorMessage = validationError.message || "Validation failed";
      showToast(`${errorMessage}`, "error");
      return Promise.reject(errorMessage);
    }
    if (error.isAxiosError && error.response?.status === 404) {
      const validationError = error.response.data;
      const errorMessage = validationError.message || "Validation failed";
      showToast(`${errorMessage}`, "error");
      return Promise.reject(errorMessage);
    }
    if (error.isAxiosError && error.response?.status === 500) {
      const validationError = error.response.data;
      const errorMessage = validationError.message || "Validation failed";
      showToast(`${errorMessage}`, "error");
      return Promise.reject(errorMessage);
    }
    if (error.isAxiosError && error.response?.status === 403) {
      const originalRequest = error.config as RetryableRequestConfig | undefined;
      const url = String(originalRequest?.url ?? "");

      if (
        isCsrfForbiddenResponse(error) &&
        originalRequest &&
        !url.includes("csrf/token")
      ) {
        if (!originalRequest._csrfRetry) {
          originalRequest._csrfRetry = true;
          try {
            await refreshCsrfToken();
            return agent(originalRequest as AxiosRequestConfig);
          } catch {
            const msg =
              messageText(error.response?.data) ||
              "Security token refresh failed";
            showToast(`${msg}`, "error");
            return Promise.reject(error);
          }
        }
        const msg =
          messageText(error.response?.data) ||
          "Invalid security token. Try again.";
        showToast(`${msg}`, "error");
        return Promise.reject(error);
      }

      const validationError = error.response.data;
      const errorMessage = validationError.message || "Validation failed";
      showToast(`${errorMessage}`, "error");
      setTimeout(function () {
        forceLogout();
      }, 3000);
      return Promise.reject(errorMessage);
    }
    if (error.isAxiosError && error.response?.status === 401) {
      const originalRequest = error.config as RetryableRequestConfig | undefined;
      const refreshToken = localStorage.getItem("refreshToken");

      // If refresh itself fails or no token available, logout immediately.
      if (
        !refreshToken ||
        originalRequest?._retry ||
        originalRequest?.url?.includes(API.ADMIN_REFRESH_TOKEN)
      ) {
        forceLogout();
        return Promise.reject(error);
      }

      try {
        if (!originalRequest) {
          forceLogout();
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = agent
            .post(API.ADMIN_REFRESH_TOKEN, { refreshToken })
            .then((response) => {
              const newAccessToken = response?.data?.data?.accessToken;
              const newRefreshToken = response?.data?.data?.refreshToken;

              if (!newAccessToken || !newRefreshToken) {
                throw new Error("Invalid refresh token response");
              }

              localStorage.setItem("authToken", newAccessToken);
              localStorage.setItem("refreshToken", newRefreshToken);
              return newAccessToken;
            })
            .finally(() => {
              isRefreshing = false;
            });
        }

        const newAccessToken = await refreshPromise;
        if (!newAccessToken) {
          throw new Error("Unable to refresh access token");
        }
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return agent(originalRequest);
      } catch (refreshError) {
        forceLogout();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default agent;
